<template>
  <CCard v-if="temConteudo">
    <CCardHeader>Como funciona</CCardHeader>

    <CCardBody>
      <p v-if="descricao" class="resumo">{{ descricao }}</p>

      <template v-if="passos.length">
        <div class="secao-titulo">Para colocar no ar</div>
        <ol class="passos">
          <li v-for="(passo, indice) in passos" :key="indice" class="passo">
            <span class="passo__numero">{{ indice + 1 }}</span>
            <div class="passo__conteudo">
              <div class="passo__titulo">{{ passo.titulo }}</div>
              <p class="passo__descricao">{{ passo.descricao }}</p>
              <LinkDoPasso
                v-if="passo.link"
                :href="passo.link"
                class="passo__link"
              >
                {{ passo.link_rotulo || TEXTOS.ABRIR_NO_SERVICO }}
              </LinkDoPasso>
            </div>
          </li>
        </ol>
      </template>

      <!--
        Exemplo escrito: um payload de verdade e o que ele vira. Vai em bloco
        monoespaçado porque o conteúdo é JSON e correspondência de campos — em
        texto corrido, ninguém consegue conferir nome por nome.
      -->
      <template v-if="exemplo">
        <div class="secao-titulo">{{ exemplo.titulo || 'Exemplo' }}</div>
        <pre class="exemplo-texto">{{ exemplo.descricao }}</pre>
      </template>

      <template v-if="porSistema.length">
        <div class="secao-titulo">Onde aparece depois de configurada</div>
        <div v-for="grupo in porSistema" :key="grupo.sistema" class="sistema">
          <div class="sistema__nome">{{ grupo.sistema }}</div>
          <ul class="sistema__locais">
            <li v-for="(local, indice) in grupo.locais" :key="indice" class="local">
              <div class="local__onde">{{ local.onde }}</div>
              <p class="local__efeito">{{ local.o_que_acontece }}</p>
            </li>
          </ul>
        </div>
      </template>

      <template v-if="imagens.length">
        <div class="secao-titulo">Como fica na tela</div>
        <figure v-for="(imagem, indice) in imagens" :key="indice" class="exemplo">
          <button type="button" class="exemplo__botao" @click="ampliada = imagem">
            <img
              :src="imagem.arquivo"
              :alt="imagem.legenda || 'Exemplo de uso'"
              loading="lazy"
            />
          </button>
          <figcaption v-if="imagem.legenda">{{ imagem.legenda }}</figcaption>
        </figure>
      </template>

      <div v-if="observacao" class="observacao">
        <fa-icon icon="info-circle" />
        <span>{{ observacao }}</span>
      </div>
    </CCardBody>

    <!--
      A miniatura cabe na coluna lateral, mas nela não se enxerga o botão que a
      legenda descreve. Clicar abre a imagem no tamanho em que foi tirada.

      Em modal, e não num overlay próprio: o cartão fica dentro de um contêiner
      com transform, e ali `position: fixed` passa a se medir pelo contêiner —
      o fundo escuro cobriria só um pedaço da tela.
    -->
    <CModal
      :show="!!ampliada"
      size="xl"
      :centered="true"
      :title="ampliada ? ampliada.legenda : ''"
      @update:show="ampliada = null"
    >
      <img v-if="ampliada" :src="ampliada.arquivo" :alt="ampliada.legenda" class="ampliacao__imagem" />

      <template #footer>
        <CButton color="secondary" variant="outline" @click="ampliada = null">Fechar</CButton>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import TEXTOS from '@/views/Integracoes/textos';
/*
 * Mini tutorial do conector.
 *
 * Substitui a descrição corrida que estava aqui antes, que dizia o que a
 * integração é mas não respondia às duas perguntas de quem abre a tela: o que
 * preciso fazer para ligar isso, e onde o recurso vai aparecer depois. Sem a
 * segunda, o usuário configura e não sabe onde conferir se funcionou.
 */
import LinkDoPasso from './LinkDoPasso.vue';

export default {
  name: 'TutorialIntegracao',
  components: { LinkDoPasso },
  props: {
    // Manifesto do conector; o tutorial vive dentro dele.
    manifesto: {
      type: Object,
      default: () => ({}),
    },
    descricao: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // Exposto para o template: as frases que outras telas do módulo repetem.
      TEXTOS,
      // Imagem aberta em tamanho real, quando houver.
      ampliada: null,
    };
  },
  computed: {
    tutorial() {
      return (this.manifesto && this.manifesto.tutorial) || {};
    },

    passos() {
      return this.tutorial.passos || [];
    },

    locais() {
      return this.tutorial.onde_aparece || [];
    },

    /*
     * Um cabeçalho por sistema, com os pontos dele embaixo.
     *
     * A etiqueta ao lado de cada linha repetia o mesmo nome três vezes e, com
     * "VersoCommerce" ocupando metade da coluna, sobrava um vão para o texto
     * que fazia cada item quebrar numa largura diferente.
     */
    porSistema() {
      const grupos = [];

      this.locais.forEach((local) => {
        const existente = grupos.find((grupo) => grupo.sistema === local.sistema);

        if (existente) {
          existente.locais.push(local);
          return;
        }

        grupos.push({ sistema: local.sistema, locais: [local] });
      });

      return grupos;
    },

    imagens() {
      return this.tutorial.imagens || [];
    },

    observacao() {
      return this.tutorial.observacao || '';
    },

    exemplo() {
      const exemplo = this.tutorial.exemplo;

      return exemplo && exemplo.descricao ? exemplo : null;
    },

    temConteudo() {
      return !!(
        this.descricao
        || this.passos.length
        || this.locais.length
        || this.imagens.length
        || this.exemplo
      );
    },
  },
};
</script>

<style scoped>
.exemplo__botao {
  background: none;
  border: 0;
  cursor: zoom-in;
  display: block;
  padding: 0;
  width: 100%;
}

.resumo {
  margin: 0 0 18px;
  font-size: 13px;
  color: #475467;
  line-height: 1.6;
  white-space: pre-line;
}

.secao-titulo {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #667085;
  padding-bottom: 6px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.secao-titulo:not(:first-child) {
  margin-top: 22px;
}

.passos {
  list-style: none;
  margin: 0;
  padding: 0;
}

.passo {
  display: flex;
  gap: 12px;
  padding-bottom: 14px;
}

.passo__numero {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #eceaf3;
  color: #3a3e81;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.passo__titulo {
  font-size: 13px;
  font-weight: 600;
  color: #1d2939;
}

.passo__descricao {
  margin: 3px 0 0;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

.passo__link {
  display: inline-block;
  margin-top: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #2298a3;
}

.sistema {
  margin-bottom: 14px;
}

.sistema__nome {
  display: inline-block;
  margin-bottom: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #edf8f9;
  color: #17727b;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.sistema__locais {
  list-style: none;
  margin: 0;
  padding: 0 0 0 10px;
  border-left: 2px solid #eceaf3;
}

.local {
  padding: 0 0 8px 8px;
}

.local:last-child {
  padding-bottom: 0;
}

.local__onde {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.local__efeito {
  margin: 2px 0 0;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

.exemplo-texto {
  background: #f8f9fb;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  color: #344054;
  font-size: 12px;
  line-height: 1.6;
  margin: 0 0 14px;
  overflow-x: auto;
  padding: 12px 14px;
  white-space: pre-wrap;
  word-break: break-word;
}

.exemplo {
  margin: 0 0 14px;
}

.exemplo img {
  display: block;
  width: 100%;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  cursor: zoom-in;
}

.exemplo figcaption {
  margin-top: 6px;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

.ampliacao__imagem {
  display: block;
  max-width: 100%;
  /* A janela inteira menos cabeçalho e rodapé do modal: a imagem cabe sem
     precisar rolar, que é o motivo de tê-la ampliado. */
  max-height: 72vh;
  margin: 0 auto;
  border-radius: 6px;
}

.observacao {
  display: flex;
  gap: 8px;
  margin-top: 18px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef8e7;
  color: #8a6d1f;
  font-size: 12px;
  line-height: 1.5;
}
</style>
